import { useMemo } from 'react';
import type { MsrAnnahme, MsrAnnahmenliste } from './typen';
import { HERKUNFT } from './gemeinsam';
import {
  ContentSection, DataTable, EmptyState, ErrorState, FilterBar, KpiCard, KpiReihe, LoadingState, StatusBadge,
  bestaetigung, herkunft as herkunftStatus, useFilterInUrl, type Fachstatus, type FilterDefinition, type Spalte,
  type Vorbehalt, annahmenVorbehalt,
} from '../../muster';
import type { MsrHerkunft } from './typen';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ANNAHMEN UND VORGABEN DER GEBÄUDEAUTOMATION (TM-8b, TX-STANDARD S1.6)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Reiter unter „Grundlagen“. Die Liste kommt fertig vom Server
 * (`GET …/msr/annahmen`) und ist DIESELBE, die als Blatt „Annahmen“ in den
 * Excel-Mappen steht (Kabel und Klemmen, Effizienzklasse, M-Bus,
 * Schrankbilanz, ISP-Listen); dazu je übrigem Schlüssel der Einstellungen und
 * Festlegungen eine Zeile. Bestätigt gilt nach F-5: Die Festlegung führt, bei
 * Abweichung der strengere Stand. Die Seite bestätigt nichts - sie zeigt den
 * Stand; bestätigt wird über die Festlegungen bzw. Einstellungen.
 */

const ANKER = 'annahmen-und-vorgaben';

/** Herkunft eines Werts nach S1.5: belegt neutral, geschätzt/Vorgabe/Annahme als Warnung. */
export function annahmeHerkunft(h: string): Fachstatus {
  if (h === 'geschaetzt') return { zustand: 'warnung', text: 'geschätzt' };
  if (h in HERKUNFT) return { zustand: 'neutral', text: HERKUNFT[h as MsrHerkunft] };
  return herkunftStatus(h);
}

/** Der Vorbehalt „n von m Annahmen unbestätigt“ für den Kopf - ohne offene Annahme keiner. */
export function msrAnnahmenVorbehalt(liste: MsrAnnahmenliste | null | undefined, ziel?: string): Vorbehalt | null {
  if (!liste || liste.offen === 0) return null;
  return annahmenVorbehalt(liste.offen, liste.gesamt, 'Annahmen und Vorgaben', ziel);
}

export const ANNAHMEN_FILTER: FilterDefinition[] = [
  {
    schluessel: 'bestaetigung', beschriftung: 'Bestätigung', art: 'auswahl',
    optionen: [
      { wert: 'unbestaetigt', beschriftung: 'unbestätigt' },
      { wert: 'bestaetigt', beschriftung: 'bestätigt' },
    ],
  },
];

/** Den Filter anwenden - ohne React, deshalb einzeln prüfbar. */
export function filtereAnnahmen(zeilen: MsrAnnahme[], f: Record<string, unknown>): MsrAnnahme[] {
  return zeilen.filter((z) => (f.bestaetigung !== 'unbestaetigt' || !z.bestaetigt)
    && (f.bestaetigung !== 'bestaetigt' || z.bestaetigt));
}

const SPALTEN: Spalte<MsrAnnahme>[] = [
  {
    schluessel: 'parameter', titel: 'Parameter', immerSichtbar: true,
    wert: (z) => z.parameter,
    inhalt: (z) => (
      <span>
        {z.parameter}
        {z.schluessel && z.schluessel !== z.parameter && <span className="schwach klein"> ({z.schluessel})</span>}
      </span>
    ),
  },
  { schluessel: 'wert', titel: 'Wert', wert: (z) => z.wert ?? '—' },
  { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.einheit ?? '' },
  {
    schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
    wert: (z) => annahmeHerkunft(z.herkunft).text,
    inhalt: (z) => <StatusBadge {...annahmeHerkunft(z.herkunft)} klein />,
  },
  {
    schluessel: 'quelle', titel: 'Quelle/Norm',
    wert: (z) => [z.quelle, z.normbezug].filter(Boolean).join(' · ') || '—',
  },
  {
    schluessel: 'bestaetigt', titel: 'Bestätigung', art: 'status',
    wert: (z) => bestaetigung(z.bestaetigt).text,
    inhalt: (z) => <StatusBadge {...bestaetigung(z.bestaetigt)} klein />,
  },
];

export function Annahmen({ liste, fehler, beiErneutVersuchen }: {
  liste: MsrAnnahmenliste | null;
  fehler?: string | null;
  beiErneutVersuchen: () => void;
}) {
  const [werte, setWerte] = useFilterInUrl(ANNAHMEN_FILTER, 'a');
  const gezeigt = useMemo(() => filtereAnnahmen(liste?.zeilen ?? [], werte), [liste, werte]);

  if (fehler) {
    return (
      <ErrorState
        titel="Die Annahmen und Vorgaben konnten nicht geladen werden."
        meldung="Ohne die Liste ist nicht zu sehen, welche Vorgaben bestätigt sind - das ist ein Fehler, kein Ergebnis."
        technisch={fehler}
        beiErneutVersuchen={beiErneutVersuchen}
      />
    );
  }
  if (!liste) return <LoadingState text="Annahmen und Vorgaben abrufen …" zeilen={8} spalten={4} />;

  return (
    <>
      <KpiReihe>
        <KpiCard titel="Annahmen und Vorgaben" wert={liste.gesamt} />
        <KpiCard titel="unbestätigt" wert={liste.offen} vergleich={`von ${liste.gesamt}`} />
        <KpiCard titel="Einstellungen des Projekts" wert={liste.einstellungen} />
        <KpiCard titel="geltende Festlegungen" wert={liste.festlegungen} vergleich="global und Projekt" />
      </KpiReihe>

      <ContentSection
        id={ANKER}
        titel="Annahmen und Vorgaben"
        beschreibung={'Dieselbe Liste wie das Blatt „Annahmen“ der Excel-Mappen, je Mappe gruppiert; darunter die '
          + 'übrigen Einstellungen und Festlegungen. Bestätigt nach F-5: Die Festlegung führt, bei Abweichung gilt '
          + '„unbestätigt“.'}
      >
        <FilterBar filter={ANNAHMEN_FILTER} werte={werte} beiAenderung={setWerte} />
        <DataTable
          name={`Annahmen (${gezeigt.length})`}
          spalten={SPALTEN}
          zeilen={gezeigt}
          schluessel={(z) => `${z.thema}|${z.schluessel ?? z.parameter}`}
          gruppierung={{ nach: (z) => z.thema }}
          dicht
          ausfuhr
          ausfuhrDateiname="msr-annahmen-und-vorgaben.csv"
          leer={(
            <EmptyState
              titel={liste.zeilen.length === 0 ? 'Keine Annahme und keine Vorgabe hinterlegt.' : 'Keine Annahme passt zum Filter.'}
              erklaerung={liste.zeilen.length === 0
                ? 'Die Vorgaben kommen aus den Einstellungen und Festlegungen des Projekts.'
                : 'Filter zurücksetzen, um alle Annahmen zu sehen.'}
            />
          )}
        />
      </ContentSection>
    </>
  );
}
